import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import { courseModules } from "../data/courseData";
import { EINDTOETS, EXAM_META } from "../data/examData";
import { useAuth } from "../context/AuthContext";
import {
  ChevronRight, ChevronLeft, CheckCircle, Lock, Play,
  BookOpen, Award, Star, Home, RotateCcw, Trophy, X,
  ClipboardList, ArrowRight, Menu, LogOut, Download
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";
const API = process.env.REACT_APP_BACKEND_URL;

// ─── COURSE NAVBAR ────────────────────────────────────────────────────────────
function CourseNav({ userName, sidebarOpen, setSidebarOpen, onLogout }) {
  return (
    <nav className="course-navbar">
      <div className="flex items-center gap-3">
        <button className="lg:hidden text-white p-1" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <Menu size={20} />
        </button>
        <img src={LOGO_URL} alt="FitSmith" className="h-8 w-8 object-contain" />
        <span className="text-white font-bold text-base tracking-wide">FitSmith <span className="text-[#C9A84C]">PT Kickstart</span></span>
      </div>
      <div className="flex items-center gap-3">
        {userName && <span className="text-zinc-400 text-sm hidden sm:block">Welkom, <strong className="text-white">{userName}</strong></span>}
        <a href="/" className="btn-course-outline text-xs px-3 py-1.5 flex items-center gap-1.5">
          <Home size={13} /> Website
        </a>
        <button onClick={onLogout} className="btn-course-outline text-xs px-3 py-1.5 flex items-center gap-1.5">
          <LogOut size={13} /> Uitloggen
        </button>
      </div>
    </nav>
  );
}


// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ progress, currentView, onNavigate, isOpen, onClose }) {
  const totalLessons = courseModules.reduce((a, m) => a + m.lessons.length, 0);
  const completedLessons = Object.keys(progress.lessons || {}).length;
  const pct = Math.round((completedLessons / totalLessons) * 100);

  return (
    <>
      {isOpen && <div className="lg:hidden fixed inset-0 bg-black/60 z-20" onClick={onClose} />}
      <aside className={`course-sidebar ${isOpen ? "course-sidebar-open" : ""}`}>
        {/* Progress */}
        <div className="p-4 border-b border-white/5">
          <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
            <span>Voortgang</span><span>{pct}%</span>
          </div>
          <div className="course-progress-bar">
            <div className="course-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-zinc-600 text-xs mt-1.5">{completedLessons}/{totalLessons} lessen voltooid</p>
        </div>

        {/* Modules */}
        <div className="flex-1 overflow-y-auto py-3">
          {/* Dashboard link */}
          <button
            onClick={() => { onNavigate("dashboard"); onClose(); }}
            className={`sidebar-link ${currentView === "dashboard" ? "sidebar-link-active" : ""}`}
          >
            <Home size={14} /> Overzicht
          </button>

          {courseModules.map(mod => {
            const modQuizDone = (progress.quizzes || {})[mod.id];
            const allLessonsDone = mod.lessons.every(l => (progress.lessons || {})[l.id]);
            return (
              <div key={mod.id} className="mb-2">
                <div className="sidebar-module-title">
                  {allLessonsDone && <CheckCircle size={12} className="text-[#C9A84C]" />}
                  Module {mod.id}
                </div>
                {mod.lessons.map(les => {
                  const done = (progress.lessons || {})[les.id];
                  return (
                    <button
                      key={les.id}
                      onClick={() => { onNavigate("lesson", mod.id, les.id); onClose(); }}
                      className={`sidebar-lesson ${currentView === `lesson-${les.id}` ? "sidebar-lesson-active" : ""}`}
                    >
                      {done ? <CheckCircle size={12} className="text-[#C9A84C]" /> : <div className="w-3 h-3 rounded-full border border-zinc-600" />}
                      <span className="truncate">{les.title}</span>
                    </button>
                  );
                })}
                {/* Quiz link */}
                <button
                  onClick={() => { onNavigate("quiz", mod.id); onClose(); }}
                  className={`sidebar-lesson sidebar-quiz-link ${currentView === `quiz-${mod.id}` ? "sidebar-lesson-active" : ""}`}
                >
                  {modQuizDone ? <CheckCircle size={12} className="text-[#C9A84C]" /> : <ClipboardList size={12} className="text-zinc-500" />}
                  <span>Moduletoets {mod.id}</span>
                  {!allLessonsDone && <Lock size={10} className="text-zinc-600 ml-auto" />}
                </button>
              </div>
            );
          })}

          {/* Final exam */}
          <div className="mt-2 border-t border-white/5 pt-2">
            <button
              onClick={() => { onNavigate("eindtoets"); onClose(); }}
              className={`sidebar-link ${currentView === "eindtoets" ? "sidebar-link-active" : ""} text-[#C9A84C]`}
            >
              <Trophy size={14} /> Eindtoets
              {!Object.keys(progress.quizzes || {}).length && <Lock size={10} className="text-zinc-600 ml-auto" />}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ progress, onNavigate, userName }) {
  const totalLessons = courseModules.reduce((a, m) => a + m.lessons.length, 0);
  const completedLessons = Object.keys(progress.lessons || {}).length;

  const getNextLesson = () => {
    for (const mod of courseModules) {
      for (const les of mod.lessons) {
        if (!(progress.lessons || {})[les.id]) return { modId: mod.id, lesId: les.id };
      }
    }
    return null;
  };
  const next = getNextLesson();

  return (
    <div className="course-content">
      <div className="mb-8">
        <p className="text-[#C9A84C] text-sm font-bold uppercase tracking-widest mb-1">Dashboard</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Welkom, {userName}!</h1>
        <p className="text-zinc-400 text-sm">Cursus: FitSmith PT Kickstart Program</p>
      </div>

      {/* Progress overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Lessen voltooid", val: `${completedLessons}/${totalLessons}`, icon: <BookOpen size={18} /> },
          { label: "Moduletoetsen", val: `${Object.keys(progress.quizzes || {}).length}/4`, icon: <ClipboardList size={18} /> },
          { label: "Eindtoets", val: (progress.final_exam || 0) >= 70 ? "Gehaald" : "Nog open", icon: <Trophy size={18} /> },
          { label: "Certificaat", val: (progress.final_exam || 0) >= 70 ? "Beschikbaar" : "Nog niet", icon: <Award size={18} /> },
        ].map(({ label, val, icon }) => (
          <div key={label} className="course-stat-card">
            <div className="text-[#C9A84C] mb-2">{icon}</div>
            <div className="text-white font-bold text-lg">{val}</div>
            <div className="text-zinc-500 text-xs">{label}</div>
          </div>
        ))}
      </div>

      {/* Continue button */}
      {next && (
        <div className="course-card p-6 mb-8 border-[#C9A84C]/20">
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-2">Ga verder waar je gebleven bent</p>
          <h3 className="text-white font-bold text-lg mb-3">
            Module {next.modId} — Les {courseModules[next.modId - 1].lessons.find(l => l.id === next.lesId)?.title}
          </h3>
          <button onClick={() => onNavigate("lesson", next.modId, next.lesId)} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase tracking-wide">
            <Play size={14} className="inline mr-1" /> Ga verder
          </button>
        </div>
      )}

      {/* Modules overview */}
      <h2 className="text-xl font-bold text-white mb-4">Alle modules</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {courseModules.map(mod => {
          const done = mod.lessons.filter(l => (progress.lessons || {})[l.id]).length;
          const quizDone = (progress.quizzes || {})[mod.id];
          return (
            <div key={mod.id} className="course-module-card" onClick={() => onNavigate("lesson", mod.id, mod.lessons[0].id)}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Module {mod.id}</p>
                  <h3 className="text-white font-bold text-base mt-0.5">{mod.title}</h3>
                  <p className="text-zinc-500 text-xs mt-0.5">{mod.subtitle}</p>
                </div>
                {done === mod.lessons.length && quizDone && (
                  <CheckCircle size={20} className="text-[#C9A84C] shrink-0" />
                )}
              </div>
              <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
                <span>{done}/{mod.lessons.length} lessen</span>
                {quizDone && <span className="text-[#C9A84C]">Toets gehaald</span>}
              </div>
              <div className="course-progress-bar">
                <div className="course-progress-fill" style={{ width: `${(done / mod.lessons.length) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── LESSON VIEW ──────────────────────────────────────────────────────────────
function LessonView({ moduleId, lessonId, progress, onComplete, onNavigate }) {
  const mod = courseModules.find(m => m.id === moduleId);
  const les = mod?.lessons.find(l => l.id === lessonId);
  const lesIdx = mod?.lessons.indexOf(les) ?? 0;
  const isCompleted = (progress.lessons || {})[lessonId];

  const prevLes = lesIdx > 0 ? mod.lessons[lesIdx - 1] : null;
  const nextLes = lesIdx < mod.lessons.length - 1 ? mod.lessons[lesIdx + 1] : null;
  const allLessonsDone = mod?.lessons.every(l => (progress.lessons || {})[l.id]);

  if (!les) return <div className="course-content text-zinc-400">Les niet gevonden.</div>;

  return (
    <div className="course-content">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-6">
        <button onClick={() => onNavigate("dashboard")} className="hover:text-white">Overzicht</button>
        <ChevronRight size={12} />
        <span className="text-zinc-400">Module {moduleId}</span>
        <ChevronRight size={12} />
        <span className="text-white">Les {lessonId}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Module {moduleId} — Les {lesIdx + 1}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{les.title}</h1>
          <p className="text-zinc-500 text-sm mt-1">{les.duration} leestijd</p>
        </div>
        {isCompleted && (
          <div className="flex items-center gap-1.5 text-[#C9A84C] text-sm font-semibold shrink-0">
            <CheckCircle size={16} /> Voltooid
          </div>
        )}
      </div>

      {/* Lesson content */}
      <div className="lesson-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(les.content, {
        ADD_TAGS: ['img', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
        ADD_ATTR: ['src', 'alt', 'style', 'width', 'height', 'class']
      }) }} />

      {/* Mark complete / navigation */}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-10 pt-6 border-t border-white/5">
        <div className="flex items-center gap-3">
          {prevLes && (
            <button onClick={() => onNavigate("lesson", moduleId, prevLes.id)} className="btn-course-outline text-sm px-4 py-2 flex items-center gap-1.5">
              <ChevronLeft size={14} /> Vorige
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!isCompleted && (
            <button onClick={() => onComplete(lessonId)} data-testid="complete-lesson-btn" className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase tracking-wide">
              Les voltooien <CheckCircle size={14} className="inline ml-1" />
            </button>
          )}
          {nextLes ? (
            <button onClick={() => { if (!isCompleted) onComplete(lessonId); onNavigate("lesson", moduleId, nextLes.id); }} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase tracking-wide">
              Volgende <ChevronRight size={14} className="inline ml-1" />
            </button>
          ) : (
            allLessonsDone && (
              <button onClick={() => onNavigate("quiz", moduleId)} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase tracking-wide flex items-center gap-1.5">
                <ClipboardList size={14} /> Moduletoets maken
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── QUIZ VIEW ────────────────────────────────────────────────────────────────
function QuizView({ moduleId, progress, onQuizComplete, onNavigate }) {
  const mod = courseModules.find(m => m.id === moduleId);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const allDone = mod?.lessons.every(l => (progress.lessons || {})[l.id]);
  const alreadyDone = (progress.quizzes || {})[moduleId];

  if (!allDone) {
    return (
      <div className="course-content text-center py-16">
        <Lock size={40} className="text-zinc-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Toets vergrendeld</h2>
        <p className="text-zinc-400 text-sm mb-6">Voltooi eerst alle lessen van Module {moduleId}.</p>
        <button onClick={() => onNavigate("lesson", moduleId, mod.lessons[0].id)} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase">
          Naar de lessen
        </button>
      </div>
    );
  }

  // Normalize open antwoord: verwijder spaties rond koppelteken, lowercase
  const normalizeOpen = (val) =>
    String(val || "").trim().toLowerCase().replace(/\s*[-–]\s*/g, "-").replace(/\s+/g, "");

  const isOpenCorrect = (q, ans) => {
    if (ans === undefined || ans === "") return false;
    const given = normalizeOpen(ans);
    const correct = normalizeOpen(q.correctAnswer);
    // Accepteer ook streepjes-varianten: "1-5" == "1–5"
    return given === correct || given === correct.replace("-", "–") || given === correct.replace("–", "-");
  };

  const handleSubmit = () => {
    let correct = 0;
    mod.quiz.forEach((q, i) => {
      if (q.type === "open") {
        if (isOpenCorrect(q, answers[i])) correct++;
      } else {
        if (answers[i] === q.correct) correct++;
      }
    });
    const s = Math.round((correct / mod.quiz.length) * 100);
    setScore(s);
    setSubmitted(true);
    if (s >= 70) onQuizComplete(moduleId, s);
  };

  const isAnswered = (q, qi) => {
    if (q.type === "open") return String(answers[qi] || "").trim().length > 0;
    return answers[qi] !== undefined;
  };

  const answeredCount = mod?.quiz.filter((q, qi) => isAnswered(q, qi)).length || 0;

  const passed = score >= 70;

  return (
    <div className="course-content">
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-6">
        <button onClick={() => onNavigate("dashboard")} className="hover:text-white">Overzicht</button>
        <ChevronRight size={12} />
        <span className="text-white">Moduletoets {moduleId}</span>
      </div>

      <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Moduletoets</p>
      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Module {moduleId}: {mod.title}</h1>
      <p className="text-zinc-400 text-sm mb-8">{mod.quiz.length} vragen — minimaal 70% voor een voldoende</p>

      {alreadyDone && !submitted && (
        <div className="course-card p-4 mb-6 border-[#C9A84C]/20 flex items-center gap-3">
          <CheckCircle size={18} className="text-[#C9A84C]" />
          <p className="text-zinc-300 text-sm">Je hebt deze toets al behaald met {alreadyDone}%. Je kunt hem nogmaals maken.</p>
        </div>
      )}

      {submitted ? (
        <div className="course-card p-8 text-center">
          <div className={`quiz-score-circle ${passed ? "quiz-pass" : "quiz-fail"}`}>{score}%</div>
          <h2 className="text-2xl font-bold text-white mb-2 mt-6">{passed ? "Geslaagd!" : "Niet voldoende"}</h2>
          <p className="text-zinc-400 text-sm mb-8">{passed ? `Goed gedaan! Je hebt moduletoets ${moduleId} gehaald.` : "Je hebt minimaal 70% nodig. Probeer het opnieuw."}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            {!passed && (
              <button onClick={() => { setAnswers({}); setSubmitted(false); }} className="btn-course-outline px-5 py-2.5 text-sm font-bold">
                <RotateCcw size={14} className="inline mr-1" /> Opnieuw proberen
              </button>
            )}
            {passed && moduleId < 4 && (
              <button onClick={() => onNavigate("lesson", moduleId + 1, courseModules[moduleId].lessons[0].id)} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase">
                Module {moduleId + 1} starten <ArrowRight size={14} className="inline ml-1" />
              </button>
            )}
            {passed && moduleId === 4 && (
              <button onClick={() => onNavigate("eindtoets")} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase">
                <Trophy size={14} className="inline mr-1" /> Naar eindtoets
              </button>
            )}
            <button onClick={() => onNavigate("dashboard")} className="btn-course-outline px-5 py-2.5 text-sm font-bold">
              Terug naar overzicht
            </button>
          </div>

          {/* Answer review */}
          <div className="mt-10 text-left space-y-4">
            <h3 className="text-white font-bold text-base">Antwoorden bekijken</h3>
            {mod.quiz.map((q, i) => {
              const correct = q.type === "open" ? isOpenCorrect(q, answers[i]) : answers[i] === q.correct;
              return (
                <div key={i} className={`quiz-review-item ${correct ? "quiz-review-correct" : "quiz-review-wrong"}`}>
                  <p className="text-white text-sm font-semibold mb-2">{i + 1}. {q.question}</p>
                  {q.type === "open" ? (
                    <div className="space-y-1">
                      <div className={`quiz-review-option ${correct ? "text-green-400" : "text-red-400"}`}>
                        {correct ? "✓" : "✗"} Jouw antwoord: <strong>{answers[i] || "—"}</strong>
                      </div>
                      {!correct && (
                        <div className="quiz-review-option text-green-400">
                          ✓ Correct antwoord: <strong>{q.correctAnswer}</strong>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {q.type === "image_mc" && <img src={q.image} alt="Afbeelding vraag" className="w-full max-w-xs mx-auto rounded-xl mb-3 block" />}
                      {q.options.map((opt, oi) => (
                        <div key={oi} className={`quiz-review-option ${oi === q.correct ? "text-green-400" : oi === answers[i] && !correct ? "text-red-400" : "text-zinc-500"}`}>
                          {oi === q.correct ? "✓" : oi === answers[i] && !correct ? "✗" : "○"} {opt}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {mod.quiz.map((q, qi) => (
            <div key={qi} className="course-card p-6">
              {/* Casus scenario block */}
              {q.type === "casus" && (
                <div className="bg-[#1a1209] border border-[#C9A84C]/25 rounded-xl p-4 mb-4">
                  <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-2">Casus</p>
                  <p className="text-zinc-300 text-sm leading-relaxed">{q.casus}</p>
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <p className="text-white font-semibold text-sm flex-1">{qi + 1}. {q.question}</p>
                {q.type === "casus" && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#C9A84C]/10 text-[#C9A84C] shrink-0">Casus</span>
                )}
                {q.type === "open" && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 shrink-0">Open vraag</span>
                )}
              </div>

              {q.type === "open" ? (
                <div className="space-y-2">
                  <input type="text" data-testid={`quiz-q${qi}-open`} placeholder={q.hint || "Typ jouw antwoord..."} value={answers[qi] || ""} onChange={(e) => setAnswers({ ...answers, [qi]: e.target.value })} className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#C9A84C] rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder:text-zinc-600" />
                  {answers[qi] && <p className="text-zinc-500 text-xs">Ingevuld: <span className="text-zinc-300">{answers[qi]}</span></p>}
                </div>
              ) : (
                <div className="space-y-2">
                  {q.type === "image_mc" && <img src={q.image} alt="Spier aanduiding" className="w-full max-w-xs mx-auto rounded-xl mb-4 block" />}
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers({ ...answers, [qi]: oi })} data-testid={`quiz-q${qi}-opt${oi}`} className={`quiz-option ${answers[qi] === oi ? "quiz-option-selected" : ""}`}>
                      <div className={`quiz-radio ${answers[qi] === oi ? "quiz-radio-selected" : ""}`} />
                      <span className="text-sm">{opt}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button
            onClick={handleSubmit}
            disabled={answeredCount < mod.quiz.length}
            data-testid="quiz-submit-btn"
            className="btn-gold-course w-full py-3.5 text-sm font-bold uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Toets inleveren ({answeredCount}/{mod.quiz.length} beantwoord)
          </button>
        </div>
      )}
    </div>
  );
}

// ─── FINAL EXAM ───────────────────────────────────────────────────────────────
function FinalExamView({ progress, onExamComplete, onNavigate, userName, onResetProgress }) {
  const allQuizzesDone = courseModules.every(m =>
    (progress.quizzes || {})[m.id] || (progress.quizzes || {})[String(m.id)]
  );
  const [phase, setPhase] = useState("intro"); // intro | exam | result
  const [moduleIdx, setModuleIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [finalScore, setFinalScore] = useState(0);

  const questionsByModule = [1, 2, 3, 4].map(m => EINDTOETS.filter(q => q.module === m));
  const mcCount = EINDTOETS.filter(q => q.type === "mc").length;
  const reasoningCount = EINDTOETS.filter(q => q.type === "reasoning").length;
  const casusCount = EINDTOETS.filter(q => q.type === "casus").length;
  const openCount = EINDTOETS.filter(q => q.type === "open").length;
  const imageMcCount = EINDTOETS.filter(q => q.type === "image_mc").length;

  // Normalize open antwoord (zelfde als moduletoets)
  const normalizeOpen = (val) =>
    String(val || "").trim().toLowerCase().replace(/\s*[-–]\s*/g, "-").replace(/\s+/g, "");
  const isOpenCorrect = (q, ans) => {
    if (!ans || String(ans).trim() === "") return false;
    return normalizeOpen(ans) === normalizeOpen(q.correctAnswer);
  };
  const isQAnswered = (q) => {
    if (q.type === "open") return String(answers[q.id] || "").trim().length > 0;
    return answers[q.id] !== undefined;
  };

  if (!allQuizzesDone) {
    return (
      <div className="course-content text-center py-16">
        <Lock size={40} className="text-zinc-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Eindtoets vergrendeld</h2>
        <p className="text-zinc-400 text-sm mb-6">Rond eerst alle vier moduletoetsen af.</p>
        <button onClick={() => onNavigate("dashboard")} className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase">
          Naar overzicht
        </button>
      </div>
    );
  }

  const calcScore = () => {
    const correct = EINDTOETS.reduce((acc, q) => {
      if (q.type === "open") return acc + (isOpenCorrect(q, answers[q.id]) ? 1 : 0);
      return acc + (answers[q.id] === q.correct ? 1 : 0);
    }, 0);
    return Math.round((correct / EINDTOETS.length) * 100);
  };

  const handleSubmitExam = () => {
    const pct = calcScore();
    setFinalScore(pct);
    setPhase("result");
    window.scrollTo(0, 0);
    onExamComplete(pct);
  };

  const allCurrentModuleAnswered = questionsByModule[moduleIdx]?.every(q => isQAnswered(q));
  const allAnswered = EINDTOETS.every(q => isQAnswered(q));

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === "intro") {
    return (
      <div className="course-content">
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Eindtoets</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Professionele Eindtoets</h1>
        <p className="text-zinc-400 text-sm mb-8">
          Personal Training Opleiding — {EINDTOETS.length} vragen — minimaal 70% voor het certificaat
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: "MC-vragen", count: mcCount, color: "text-blue-400", bg: "border-blue-500/20" },
            { label: "Redeneervragen", count: reasoningCount, color: "text-purple-400", bg: "border-purple-500/20" },
            { label: "Casusvragen", count: casusCount, color: "text-[#C9A84C]", bg: "border-[#C9A84C]/20" },
            { label: "Open invulvragen", count: openCount, color: "text-green-400", bg: "border-green-500/20" },
            { label: "Beeldvragen", count: imageMcCount, color: "text-amber-400", bg: "border-amber-500/20" },
          ].map(({ label, count, color, bg }) => (
            <div key={label} className={`course-card p-4 text-center ${bg}`}>
              <div className={`font-bold text-3xl mb-1 ${color}`}>{count}</div>
              <div className="text-zinc-400 text-xs">{label}</div>
            </div>
          ))}
        </div>

        <div className="course-card p-6 mb-6">
          <h3 className="text-white font-bold mb-4">Hoe werkt de eindtoets?</h3>
          <ul className="space-y-2.5 text-sm">
            <li className="flex gap-3 text-zinc-400"><span className="text-blue-400 font-bold shrink-0">MC-vragen</span>Kies het juiste antwoord uit vier opties — automatisch nagekeken</li>
            <li className="flex gap-3 text-zinc-400"><span className="text-purple-400 font-bold shrink-0">Redeneervragen</span>Analytische MC-vragen die inzicht in trainingsprincipes testen — ook automatisch nagekeken</li>
            <li className="flex gap-3 text-zinc-400"><span className="text-[#C9A84C] font-bold shrink-0">Casusvragen</span>Praktijkscenario's met vier antwoordopties — kies de meest correcte aanpak</li>
            <li className="flex gap-3 text-zinc-400"><span className="text-green-400 font-bold shrink-0">Open invulvragen</span>Typ zelf het juiste antwoord in — bijv. een herhalingsrange zoals 6-12</li>
            <li className="flex gap-3 text-zinc-400"><span className="text-green-400 font-bold shrink-0">Score</span>Minimaal 70% nodig voor het certificaat — bij onvoldoende wordt je voortgang gereset</li>
          </ul>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {questionsByModule.map((qs, i) => (
            <div key={i} className="course-card p-4 text-center">
              <p className="text-[#C9A84C] text-xs font-bold mb-1">Module {i + 1}</p>
              <p className="text-white font-bold text-2xl mb-0.5">{qs.length}</p>
              <p className="text-zinc-600 text-xs">vragen</p>
            </div>
          ))}
        </div>

        <button
          onClick={() => setPhase("exam")}
          data-testid="start-final-exam-btn"
          className="btn-gold-course w-full py-4 text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2"
        >
          Eindtoets starten <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  // ── EXAM ───────────────────────────────────────────────────────────────────
  if (phase === "exam") {
    const currentModuleQs = questionsByModule[moduleIdx];
    const isLastModule = moduleIdx === 3;
    return (
      <div className="course-content">
        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-4">
          <button onClick={() => setPhase("intro")} className="hover:text-white">Eindtoets</button>
          <ChevronRight size={12} />
          <span className="text-white">Module {moduleIdx + 1}</span>
        </div>
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">
          Module {moduleIdx + 1} van 4
        </p>
        <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{EXAM_META.modules[moduleIdx]}</h1>
        <p className="text-zinc-500 text-sm mb-5">{currentModuleQs.length} vragen</p>

        <div className="flex gap-1.5 mb-8">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${
              i < moduleIdx ? "bg-[#C9A84C]" : i === moduleIdx ? "bg-[#C9A84C]/50" : "bg-zinc-800"
            }`} />
          ))}
        </div>

        <div className="space-y-6">
          {currentModuleQs.map(q => {
            const globalIdx = EINDTOETS.findIndex(eq => eq.id === q.id) + 1;
            const isAnswered = isQAnswered(q);
            const typeLabel = q.type === "reasoning" ? { label: "Redeneervraag", color: "text-purple-400" }
              : q.type === "casus" ? { label: "Casusvraag", color: "text-[#C9A84C]" }
              : q.type === "open" ? { label: "Open invulvraag", color: "text-green-400" }
              : q.type === "image_mc" ? { label: "Beeldvraag", color: "text-amber-400" }
              : { label: "Multiple Choice", color: "text-blue-400" };
            return (
              <div key={q.id} className={`course-card p-6 transition-all ${isAnswered ? "border-white/15" : ""}`}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className="text-zinc-600 text-xs font-mono">V{globalIdx}</span>
                  <span className={`text-xs font-semibold ${typeLabel.color}`}>{typeLabel.label}</span>
                </div>
                {q.casus && (
                  <div className="bg-[#0a0a0a] border border-white/8 rounded-xl p-4 mb-4 text-sm text-zinc-400 leading-relaxed italic">
                    {q.casus}
                  </div>
                )}
                <p className="text-white text-sm leading-relaxed font-medium mb-4">{q.question}</p>

                {q.type === "open" ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      data-testid={`exam-q-${q.id}-open`}
                      placeholder={q.hint || "Typ jouw antwoord..."}
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                      className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#C9A84C] rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder:text-zinc-600"
                    />
                    {answers[q.id] && (
                      <p className="text-zinc-500 text-xs">Ingevuld: <span className="text-zinc-300">{answers[q.id]}</span></p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {q.type === "image_mc" && <img src={q.image} alt="Afbeelding vraag" className="w-full max-w-sm mx-auto rounded-xl mb-4 block" />}
                    {q.options.map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => setAnswers(a => ({ ...a, [q.id]: oi }))}
                        className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                          answers[q.id] === oi
                            ? "bg-[#C9A84C]/10 border-[#C9A84C]/50 text-white"
                            : "bg-[#111] border-white/8 text-zinc-400 hover:border-white/20 hover:text-white"
                        }`}
                      >
                        <span className="font-bold mr-2 text-zinc-500">{["A","B","C","D"][oi]}.</span> {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex gap-3">
          {moduleIdx > 0 && (
            <button onClick={() => { setModuleIdx(m => m - 1); window.scrollTo(0, 0); }}
              className="btn-course-outline flex-1 py-3 text-sm font-bold">
              Vorige module
            </button>
          )}
          {!isLastModule ? (
            <button
              onClick={() => { setModuleIdx(m => m + 1); window.scrollTo(0, 0); }}
              disabled={!allCurrentModuleAnswered}
              className="btn-gold-course flex-1 py-3 text-sm font-bold uppercase disabled:opacity-40"
            >
              Volgende module <ArrowRight size={14} className="inline ml-1" />
            </button>
          ) : (
            <button
              onClick={handleSubmitExam}
              disabled={!allAnswered}
              data-testid="submit-exam-btn"
              className="btn-gold-course flex-1 py-3 text-sm font-bold uppercase disabled:opacity-40"
            >
              Toets inleveren <ArrowRight size={14} className="inline ml-1" />
            </button>
          )}
        </div>
        {!allCurrentModuleAnswered && (
          <p className="text-zinc-600 text-xs text-center mt-3">
            Beantwoord alle vragen in deze module om door te gaan
          </p>
        )}
      </div>
    );
  }

  // ── RESULT ─────────────────────────────────────────────────────────────────
  const passed = finalScore >= 70;
  const totalCorrect = EINDTOETS.reduce((acc, q) => {
    if (q.type === "open") return acc + (isOpenCorrect(q, answers[q.id]) ? 1 : 0);
    return acc + (answers[q.id] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="course-content">
      <div className="course-card p-8 text-center">
        <div className={`quiz-score-circle mx-auto ${passed ? "quiz-pass" : "quiz-fail"}`}>{finalScore}%</div>
        <h2 className="text-2xl font-bold text-white mb-2 mt-6">
          {passed ? "Gecertificeerd!" : "Niet voldoende"}
        </h2>
        <p className="text-zinc-400 text-sm mb-8">
          {passed
            ? `Gefeliciteerd ${userName}! Je hebt de eindtoets gehaald en je certificaat verdiend.`
            : "Je hebt minimaal 70% nodig voor het certificaat. Je kunt de toets opnieuw proberen."}
        </p>
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="course-stat-card">
            <div className="text-green-400 font-bold text-xl">{totalCorrect}</div>
            <div className="text-zinc-600 text-xs mt-1">Correct</div>
          </div>
          <div className="course-stat-card">
            <div className="text-red-400 font-bold text-xl">{EINDTOETS.length - totalCorrect}</div>
            <div className="text-zinc-600 text-xs mt-1">Fout</div>
          </div>
          <div className="course-stat-card">
            <div className="text-[#C9A84C] font-bold text-xl">{totalCorrect}/{EINDTOETS.length}</div>
            <div className="text-zinc-600 text-xs mt-1">Totaal</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          {passed && (
            <button onClick={() => onNavigate("certificaat")} className="btn-gold-course px-6 py-3 text-sm font-bold uppercase">
              <Award size={14} className="inline mr-1" /> Bekijk certificaat
            </button>
          )}
          {!passed && (
            <button
              onClick={() => { setPhase("intro"); setAnswers({}); setFinalScore(0); }}
              data-testid="retry-exam-btn"
              className="btn-gold-course px-6 py-3 text-sm font-bold uppercase"
            >
              <RotateCcw size={14} className="inline mr-1" /> Opnieuw proberen
            </button>
          )}
          {passed && (
            <button onClick={() => onNavigate("dashboard")} className="btn-course-outline px-5 py-2.5 text-sm font-bold">
              Terug naar overzicht
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CERTIFICATE ──────────────────────────────────────────────────────────────
function CertificateView({ userName, progress }) {
  const score = progress.final_exam || 0;
  if (score < 70) return null;

  const today = new Date();
  const datumLang = today.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });

  // Uniek certificaatnummer op basis van naam + datum
  const certNr = `FS-${today.getFullYear()}-${String(
    [...(userName || "X")].reduce((a, c) => a + c.charCodeAt(0), today.getMonth() + today.getDate())
  ).slice(-4).padStart(4, "0")}`;

  // Module quizscores
  const modules = [
    { nr: 1, naam: "Fundament van Training",    onderwerpen: "Fysiologie, energiesystemen, herstel & adaptatie" },
    { nr: 2, naam: "Trainingsvariabelen",        onderwerpen: "Volume, intensiteit, frequentie & periodisering" },
    { nr: 3, naam: "Schema's en Oefeningen",    onderwerpen: "Schema-types, compound vs. isolatie, ROM" },
    { nr: 4, naam: "Coach in de Praktijk",      onderwerpen: "Intake, coaching, motivatie & plateau aanpak" },
  ];

  const qz = progress.quizzes || {};
  const grade = score >= 90 ? "Uitstekend" : score >= 80 ? "Goed" : score >= 70 ? "Voldoende" : "Onvoldoende";
  const gradeColor = score >= 90 ? "#22c55e" : score >= 80 ? "#3b82f6" : "#C9A84C";

  const totalLessons = 20;
  const totalVragen = 60;

  return (
    <div className="course-content">
      {/* Print knop */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <h2 className="text-white font-bold text-xl">Jouw Certificaat</h2>
          <p className="text-zinc-500 text-sm mt-0.5">Certificaatnummer: <span className="text-[#C9A84C] font-mono">{certNr}</span></p>
        </div>
        <button
          onClick={() => window.print()}
          data-testid="print-certificate-btn"
          className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase flex items-center gap-2"
        >
          <Download size={14} /> Opslaan als PDF
        </button>
      </div>

      {/* ── CERTIFICAAT ──────────────────────────────────────────────── */}
      <div data-testid="certificate" className="certificate-pro" id="certificate-print">

        {/* Decoratieve goudenlijn boven */}
        <div className="cert-top-bar" />

        {/* Header */}
        <div className="cert-header">
          <img src={LOGO_URL} alt="FitSmith" className="cert-logo" />
          <div>
            <p className="cert-academy-label">FitSmith Academy</p>
            <p className="cert-subtitle-label">Officieel Certificaat van Voltooiing</p>
          </div>
          <div className="cert-nr-block">
            <p className="cert-nr-label">CERTIFICAATNR.</p>
            <p className="cert-nr-value">{certNr}</p>
          </div>
        </div>

        <div className="cert-gold-line" />

        {/* Hoofdtekst */}
        <div className="cert-body">
          <p className="cert-declares">Hierbij verklaren wij dat</p>
          <h1 className="cert-name">{userName}</h1>
          <p className="cert-declares">met succes de opleiding heeft afgerond:</p>
          <h2 className="cert-course-title">PT Kickstart Program</h2>
          <p className="cert-course-sub">Personal Training Opleiding — Bouwen voor Beginnende Trainers</p>
        </div>

        <div className="cert-gold-line" />

        {/* Cursusinformatie + moduleresultaten */}
        <div className="cert-info-grid">
          {/* Links: moduleoverzicht */}
          <div className="cert-modules">
            <p className="cert-section-label">Moduleresultaten</p>
            <table className="cert-table">
              <tbody>
                {modules.map(m => {
                  const mScore = qz[m.nr] ?? qz[String(m.nr)];
                  return (
                    <tr key={m.nr} className="cert-table-row">
                      <td className="cert-table-mod">
                        <span className="cert-mod-nr">M{m.nr}</span>
                        <div>
                          <p className="cert-mod-naam">{m.naam}</p>
                          <p className="cert-mod-sub">{m.onderwerpen}</p>
                        </div>
                      </td>
                      <td className="cert-table-score">
                        {mScore !== undefined
                          ? <span className="cert-score-badge" style={{ color: mScore >= 70 ? "#22c55e" : "#ef4444" }}>{mScore}%</span>
                          : <span className="cert-score-badge">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Rechts: eindscore + details */}
          <div className="cert-score-panel">
            <p className="cert-section-label">Eindresultaat</p>

            {/* Grote eindscore cirkel */}
            <div className="cert-score-circle">
              <svg viewBox="0 0 120 120" className="cert-score-svg">
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(201,168,76,0.15)" strokeWidth="8" />
                <circle cx="60" cy="60" r="52" fill="none" stroke={gradeColor}
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 326.7} 326.7`}
                  transform="rotate(-90 60 60)" />
              </svg>
              <div className="cert-score-inner">
                <span className="cert-score-pct">{score}%</span>
                <span className="cert-score-grade" style={{ color: gradeColor }}>{grade}</span>
              </div>
            </div>

            {/* Statistieken */}
            <div className="cert-stats">
              {[
                { val: "4", label: "Modules" },
                { val: String(totalLessons), label: "Lessen" },
                { val: String(totalVragen), label: "Vragen" },
              ].map(s => (
                <div key={s.label} className="cert-stat">
                  <span className="cert-stat-val">{s.val}</span>
                  <span className="cert-stat-label">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="cert-date-block">
              <p className="cert-section-label" style={{ textAlign: "center" }}>Afgegeven op</p>
              <p className="cert-date-val">{datumLang}</p>
            </div>
          </div>
        </div>

        <div className="cert-gold-line" />

        {/* Handtekening */}
        <div className="cert-footer">
          <div className="cert-sig-block">
            <div className="cert-sig-line" />
            <p className="cert-sig-name">Sam van Grondelle</p>
            <p className="cert-sig-title">Oprichter &amp; Hoofddocent · FitSmith Academy</p>
          </div>
          <div className="cert-seal">
            <div className="cert-seal-inner">
              <img src={LOGO_URL} alt="" className="cert-seal-logo" />
              <p className="cert-seal-text">GECERTIFICEERD</p>
            </div>
          </div>
          <div className="cert-sig-block" style={{ textAlign: "right" }}>
            <div className="cert-sig-line" />
            <p className="cert-sig-name">FitSmith Academy</p>
            <p className="cert-sig-title">BTW: NL005444951B11 · info@fitsmith.nl</p>
          </div>
        </div>

        {/* Decoratieve goudenlijn onder */}
        <div className="cert-top-bar" />
      </div>

      {/* Print knop onderaan */}
      <div className="flex justify-center mt-6 print:hidden">
        <button onClick={() => window.print()}
          className="btn-course-outline px-6 py-3 text-sm font-bold flex items-center gap-2">
          <Download size={14} /> Certificaat opslaan als PDF
        </button>
      </div>
    </div>
  );
}

// ─── MAIN COURSE APP ──────────────────────────────────────────────────────────
export default function CourseApp() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [progress, setProgress] = useState({});
  const [progressLoading, setProgressLoading] = useState(true);
  const [view, setView] = useState(
    location.pathname.endsWith("/certificaat") ? "certificaat" : "dashboard"
  );
  const [currentModule, setCurrentModule] = useState(1);
  const [currentLesson, setCurrentLesson] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Load progress from backend
  const loadProgress = useCallback(async () => {
    if (!user?.token) return;
    try {
      const r = await fetch(`${API}/api/progress`, { headers: { Authorization: `Bearer ${user.token}` } });
      if (r.ok) setProgress(await r.json());
    } catch (e) { /* progress load failed silently */ }
    setProgressLoading(false);
  }, [user]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const saveToBackend = async (update) => {
    if (!user?.token) return;
    try {
      const r = await fetch(`${API}/api/progress`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify(update)
      });
      if (r.ok) setProgress(await r.json());
    } catch (e) { /* progress save failed silently */ }
  };

  const handleNavigate = (v, modId, lesId) => {
    setView(v);
    if (modId) setCurrentModule(modId);
    if (lesId) setCurrentLesson(lesId);
    window.scrollTo(0, 0);
  };

  const handleCompleteLesson = async (lesId) => {
    const updated = { lessons: { ...(progress.lessons || {}), [lesId]: true } };
    setProgress(p => ({ ...p, lessons: { ...(p.lessons || {}), [lesId]: true } }));
    await saveToBackend(updated);
  };

  const handleQuizComplete = async (modId, score) => {
    const updated = { quizzes: { ...(progress.quizzes || {}), [modId]: score } };
    setProgress(p => ({ ...p, quizzes: { ...(p.quizzes || {}), [modId]: score } }));
    await saveToBackend(updated);
  };

  const handleExamComplete = async (score) => {
    if (score >= 70) {
      // Geslaagd — sla score op
      setProgress(p => ({ ...p, final_exam: score }));
      await saveToBackend({ final_exam: score });
    } else {
      // Gezakt — sla score tijdelijk op zodat de gebruiker zijn resultaat ziet
      setProgress(p => ({ ...p, final_exam: score }));
      // Reset na 0 ms (zodat FinalExamView de score kan tonen, dan reset de rest)
      // De daadwerkelijke reset van alle voortgang gebeurt als de gebruiker op "Begin opnieuw" klikt
      await saveToBackend({ final_exam: score });
    }
  };

  const handleResetProgress = async () => {
    try {
      await fetch(`${API}/api/progress/reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${user.token}` },
      });
    } catch (e) { /* silent */ }
    const leeg = {};
    setProgress(leeg);
    setView("dashboard");
    window.scrollTo(0, 0);
  };

  const getViewKey = () => {
    if (view === "lesson") return `lesson-${currentLesson}`;
    if (view === "quiz") return `quiz-${currentModule}`;
    return view;
  };

  if (progressLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-zinc-500 text-sm">Laden...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <CourseNav
        userName={user?.name}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={logout}
      />
      <div className="flex flex-1 pt-16">
        <Sidebar
          progress={progress}
          currentView={getViewKey()}
          onNavigate={handleNavigate}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className="flex-1 lg:ml-64 overflow-y-auto">
          {view === "dashboard" && <Dashboard progress={progress} onNavigate={handleNavigate} userName={user?.name} />}
          {view === "lesson" && <LessonView moduleId={currentModule} lessonId={currentLesson} progress={progress} onComplete={handleCompleteLesson} onNavigate={handleNavigate} />}
          {view === "quiz" && <QuizView moduleId={currentModule} progress={progress} onQuizComplete={handleQuizComplete} onNavigate={handleNavigate} />}
          {view === "eindtoets" && <FinalExamView progress={progress} onExamComplete={handleExamComplete} onNavigate={handleNavigate} userName={user?.name} onResetProgress={handleResetProgress} />}
          {view === "certificaat" && <CertificateView userName={user?.name} progress={progress} />}
        </main>
      </div>
    </div>
  );
}
